import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Loader2, CheckCircle2 } from "lucide-react";

export default function WhitePaperSection() {
  const [form, setForm] = useState({ full_name: "", email: "", company: "", role: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Compila nome ed email per ricevere il documento.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/whitepaper-lead", form);
      setDone(true);
      toast.success("Lead registrato. Il White Paper è pronto al download.");
    } catch (err) {
      toast.error("Errore tecnico. Riprova fra qualche istante.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="whitepaper" className="relative py-24 lg:py-32 enea-paper-bg border-t border-slate-100" data-testid="whitepaper-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-y-10 gap-x-2 md:gap-10 lg:gap-14">
          <div className="col-span-12 lg:col-span-5">
            <div className="enea-eyebrow flex items-center gap-3 mb-5">
              <FileText className="w-4 h-4 text-[#2c7da0]" />
              <span>05 · Documento tecnico</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-[44px] font-display font-semibold leading-tight text-[#0b2545]">
              White Paper
              <span className="block mt-3 text-slate-600 font-normal text-2xl lg:text-3xl">
                Impatto Normative APE 2026
              </span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Analisi dell'impatto delle nuove direttive sulla classe energetica applicate
              al patrimonio residenziale italiano. Modello quantitativo, casistica,
              scenari di adeguamento e correlazioni con il valore di mercato MCM.
            </p>

            <div className="mt-8 rounded-xl bg-white border border-slate-200 divide-y divide-slate-100 shadow-sm">
              {[
                ["Sezioni", "07"],
                ["Pagine", "42"],
                ["Datasets allegati", "3"],
                ["Versione", "2026.01 · IT"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-5 py-3.5 text-sm">
                  <span className="text-slate-500 uppercase tracking-[0.14em] text-[11px] font-medium">{k}</span>
                  <span className="text-[#0b2545] font-semibold">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <div className="rounded-2xl bg-white border border-slate-200 p-7 lg:p-10 shadow-lg shadow-slate-200/40" data-testid="whitepaper-form-card">
              {!done ? (
                <>
                  <div className="flex items-center justify-between mb-7">
                    <div>
                      <div className="enea-eyebrow">Richiesta accesso</div>
                      <div className="mt-1 text-xl font-display font-semibold text-[#0b2545]">Compila per ricevere il documento</div>
                    </div>
                    <div className="w-11 h-11 rounded-lg bg-[#2c7da0]/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#2c7da0]" />
                    </div>
                  </div>

                  <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="whitepaper-form">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.14em] font-medium text-slate-600">Nome e cognome *</Label>
                      <Input
                        className="mt-2 bg-white border-slate-300 text-[#0b2545] focus-visible:ring-2 focus-visible:ring-[#2c7da0]/30 focus-visible:border-[#2c7da0] rounded-md h-11"
                        value={form.full_name}
                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        data-testid="wp-fullname"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.14em] font-medium text-slate-600">Email *</Label>
                      <Input
                        type="email"
                        className="mt-2 bg-white border-slate-300 text-[#0b2545] focus-visible:ring-2 focus-visible:ring-[#2c7da0]/30 focus-visible:border-[#2c7da0] rounded-md h-11"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="wp-email"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.14em] font-medium text-slate-600">Azienda</Label>
                      <Input
                        className="mt-2 bg-white border-slate-300 text-[#0b2545] focus-visible:ring-2 focus-visible:ring-[#2c7da0]/30 focus-visible:border-[#2c7da0] rounded-md h-11"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        data-testid="wp-company"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.14em] font-medium text-slate-600">Ruolo</Label>
                      <Input
                        className="mt-2 bg-white border-slate-300 text-[#0b2545] focus-visible:ring-2 focus-visible:ring-[#2c7da0]/30 focus-visible:border-[#2c7da0] rounded-md h-11"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="wp-role"
                      />
                    </div>

                    <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#0b2545] text-white hover:bg-[#13315c] disabled:opacity-60 transition-colors rounded-md font-semibold text-sm shadow-sm"
                        data-testid="wp-submit"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {loading ? "Invio in corso…" : "Sblocca il download"}
                      </button>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Nessuno spam. I dati restano interni a Gruppo Vela.
                      </p>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center py-8" data-testid="whitepaper-success">
                  <div className="w-14 h-14 rounded-full bg-[#0d9488]/10 mx-auto flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-7 h-7 text-[#0d9488]" />
                  </div>
                  <h3 className="text-2xl font-display font-semibold tracking-tight text-[#0b2545]">Accesso autorizzato.</h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Il White Paper è disponibile. Una copia ti è stata associata in archivio Home Exchange Copilot AI.
                  </p>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); toast.info("Versione finale in pubblicazione · 2026.01"); }}
                    className="mt-7 inline-flex items-center gap-3 px-6 py-3.5 bg-[#0b2545] text-white hover:bg-[#13315c] transition-colors rounded-md font-semibold text-sm shadow-sm"
                    data-testid="wp-download-link"
                  >
                    <Download className="w-4 h-4" />
                    Download White Paper · PDF
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
